import { useState, useEffect } from "react";
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Warning } from "@phosphor-icons/react";

/**
 * FirstTouchConfirmDialog (Phase 5)
 *
 * Rail de confirmación que se dispara la PRIMERA vez que un usuario del Hub
 * envía un mensaje a un trip nuevo. Evita el clásico "typo — le mandé al
 * cliente equivocado" cuando el modo outbound es `all_kimkim` y todos los
 * trips donde support@ es collaborator están habilitados.
 *
 * Contrato:
 *  - Solo se abre si `open === true`. El padre (InboxPage) es quien decide
 *    si abrirlo consultando /first-touch-status.
 *  - Al confirmar (checkbox marcado + click en "Enviar mensaje") ejecuta
 *    `onConfirm()`. El padre entonces dispara el POST /messages real.
 *  - Al cancelar cierra el diálogo SIN enviar; el mensaje queda en el
 *    composer intacto.
 *
 * Props:
 *  - open, onOpenChange       — estándar shadcn Dialog.
 *  - trip_title / customer_name / preview — datos para el resumen visual.
 *  - onConfirm                — async () -> void, ejecuta el envío.
 *  - onCancel                 — void, cierra sin enviar.
 *  - submitting               — deshabilita CTAs mientras el envío está en curso.
 */
export default function FirstTouchConfirmDialog({
    open, onOpenChange,
    trip_title = "",
    customer_name = "",
    preview = "",
    onConfirm,
    onCancel,
    submitting = false,
}) {
    const [checked, setChecked] = useState(false);

    // Reset the checkbox every time the dialog opens: forcing the agent to
    // click it again for the next new trip is the whole point of the rail.
    useEffect(() => {
        if (open) setChecked(false);
    }, [open]);

    const previewClamped = (preview || "").slice(0, 200);
    const showEllipsis = (preview || "").length > 200;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className="max-w-lg"
                data-testid="first-touch-confirm-dialog"
                // Prevent an outside-click from silently sending — the whole
                // point of the rail is a deliberate positive confirmation.
                onInteractOutside={(e) => e.preventDefault()}
                onEscapeKeyDown={(e) => { if (submitting) e.preventDefault(); }}
            >
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Warning size={18} weight="fill" className="text-amber-500" />
                        Primer mensaje a este trip
                    </DialogTitle>
                    <DialogDescription>
                        Vas a escribir por primera vez al chat de KimKim de este cliente.
                        Verificá que el destinatario y el contenido sean correctos antes de enviar.
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-3 text-sm">
                    <div className="rounded-md border border-slate-200 bg-slate-50 p-3">
                        <dl className="grid grid-cols-[6rem_1fr] gap-x-3 gap-y-1 text-xs">
                            <dt className="text-slate-500 uppercase tracking-wider">Trip</dt>
                            <dd className="text-slate-900 font-medium break-words"
                                data-testid="first-touch-trip">
                                {trip_title || "—"}
                            </dd>
                            <dt className="text-slate-500 uppercase tracking-wider">Cliente</dt>
                            <dd className="text-slate-900 font-medium break-words"
                                data-testid="first-touch-customer">
                                {customer_name || "—"}
                            </dd>
                        </dl>
                    </div>

                    <div>
                        <div className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1">
                            Contenido a enviar
                        </div>
                        <div className="rounded-md border border-slate-200 bg-white p-3 text-slate-800 text-xs whitespace-pre-wrap break-words max-h-32 overflow-y-auto"
                             data-testid="first-touch-preview">
                            {previewClamped || "(vacío)"}
                            {showEllipsis && <span className="text-slate-400">…</span>}
                        </div>
                    </div>

                    <label className="flex items-start gap-2 select-none cursor-pointer p-2 rounded hover:bg-slate-50">
                        <Checkbox
                            checked={checked}
                            onCheckedChange={(v) => setChecked(v === true)}
                            data-testid="first-touch-checkbox"
                            className="mt-0.5"
                            disabled={submitting}
                        />
                        <span className="text-xs text-slate-700 leading-relaxed">
                            He verificado el destinatario y confirmo que quiero escribir a{" "}
                            <b className="text-slate-900">{customer_name || "este cliente"}</b>{" "}
                            en el trip <b className="text-slate-900">{trip_title || "seleccionado"}</b>.
                        </span>
                    </label>
                </div>

                <DialogFooter className="gap-2">
                    <Button
                        variant="outline"
                        onClick={() => { onCancel?.(); }}
                        disabled={submitting}
                        data-testid="first-touch-cancel-btn"
                    >
                        Cancelar
                    </Button>
                    <Button
                        onClick={() => { if (checked && !submitting) onConfirm?.(); }}
                        disabled={!checked || submitting}
                        className="bg-brand-600 hover:bg-brand-700 text-white"
                        data-testid="first-touch-confirm-btn"
                    >
                        {submitting ? "Enviando…" : "Enviar mensaje"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
